import React, { useState, useEffect, useCallback, useRef, Component } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import TabbedProjectInterface from "./components/TabbedProjectInterface";
import AddProjectForm from "./components/AddProjectForm";
import SkeletonLoader from "./components/SkeletonLoader";
import TopProgressBar from "./components/TopProgressBar";
import ErrorMessage from "./components/ErrorMessage";
import Header from "./components/Header";
import ProtectedRoute from "./components/ProtectedRoute";
import AuthCallback from "./components/AuthCallback";
import FullSubitemModal from "./components/FullSubitemModal";
import ProjectDetailsModal from "./components/ProjectDetailsModal";
import SubitemsModal from "./components/SubitemsModal";
import ContactModal from "./components/ContactModal";
import ContactsTable from "./components/ContactsTable";
import ClientsTable from "./components/ClientsTable";
import Sidebar from "./components/Sidebar";
import UserProfile from "./components/UserProfile";
import AdminPanel from "./components/AdminPanel";
import PlannerConfigAdmin from "./components/Admin/PlannerConfigAdmin";
import PlannerConfigDemo from "./components/Admin/PlannerConfigAdmin/DemoPage";
import ModelPlannerPage from "./components/ModelPlanner/ModelPlannerPage";
import DiscrepancyTable from "./components/Discrepancies/DiscrepancyTable";
import ProjectSetupBoard from "./components/ProjectSetupBoard";
import SubitemTemplateTable from "./components/SubitemTemplateTable";
import SubitemTemplateManager from "./components/SubitemTemplateManager";
import TakeoffsTable from "./components/TakeoffsTable";
import OperationsDashboard from "./components/OperationsDashboard";
import ModelBuildingManagerDashboard from "./components/ModelBuildingManagerDashboard";
import ManagerDashboard from "./components/ManagerDashboard";
import OfficeAdminDashboard from "./components/OfficeAdminDashboard";
import InvoicingBoard from "./components/InvoicingBoard";
import OnHoldBoard from "./components/OnHoldBoard";
import ProjectStats from "./components/ProjectStats";
import EmployeesManagement from "./components/EmployeesManagement/EmployeesManagement";
import FeedbackTable from "./components/FeedbackTable";
import PricingDashboard from "./components/Pricing/PricingDashboard";
import PayrollBoard from "./components/PayrollBoard";
import ProjectEmailsBoard from "./components/ProjectEmailsBoard";
import ProfitAnalysis from "./components/ProfitAnalysis";
import { KPIBoard } from "./components/KPIBoard";
import RemoteSupportTable from "./components/RemoteSupport/RemoteSupportTable";
import PeerReviews from "./components/PeerReviews";
import UsageTracker from "./components/UsageTracker";
import { AuthProvider } from "./contexts/AuthContext";
import { DataProvider } from "./contexts/DataContext";
import { parseCurrentUrl, handleLegacyUrl, createNavigationHelper, generatePageUrl } from "./utils/urlManager";
import { ProjectService } from "./services/projectService";
import { StatusValidationService } from "./services/statusValidationService";
import { useUserRole } from "./hooks/useUserRoleSecure";
import { useRealtimeProjects } from "./hooks/useRealtimeProjects";
import { supabase } from "./lib/supabase";
import StatusAdvancementWarningModal from "./components/StatusAdvancementWarningModal";
import ModelingStatusExitCommentModal from "./components/ModelingStatusExitCommentModal";
import SessionWarning from "./components/SessionWarning";
import NetworkMonitor from "./components/NetworkMonitor";
import "./utils/fixEmailTracking"; // Import email tracking fix utility
import "./utils/checkEnumValues"; // Import utility to check valid enum values
import "./utils/setupAvatarStorage"; // Import utility to setup avatar storage
import "./utils/createAvatarBucket"; // Import utility to create avatar bucket
import "./utils/checkStorageSetup"; // Import utility to check storage setup
import "./utils/checkContactsTable"; // Check contacts table structure
import { Toaster } from 'react-hot-toast';
import "./App.css";
import "./styles/global-tab-highlighting.css";

const Dashboard = ({ searchTerm }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Parse current URL to get tab and project ID
  const { tab: currentTab, id: urlProjectId } = parseCurrentUrl(location.pathname);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [operationLoading, setOperationLoading] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProjectDetailsModalOpen, setIsProjectDetailsModalOpen] = useState(false);
  const [isSubitemsModalOpen, setIsSubitemsModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [undoHistory, setUndoHistory] = useState([]);
  const [showUndoToast, setShowUndoToast] = useState(false);
  const [updatingProjects, setUpdatingProjects] = useState(new Set());
  const [activeTab, setActiveTab] = useState(() => {
    // Use URL-based tab if available, otherwise load saved tab from localStorage
    return currentTab || localStorage.getItem("activeTab") || "waiting-to-start";
  });
  
  // Navigation helper
  const navigationHelper = createNavigationHelper(navigate);

  // Status advancement warning modal state
  const [showStatusWarning, setShowStatusWarning] = useState(false);
  const [statusWarningData, setStatusWarningData] = useState({
    incompleteSubitems: [],
    currentStatus: "",
    targetStatus: "",
    projectId: null,
    field: "",
    pendingValue: null,
    resolve: null,
  });

  // Modeling status exit comment modal state
  const [showExitCommentModal, setShowExitCommentModal] = useState(false);
  const [exitCommentData, setExitCommentData] = useState({
    fromStatus: "",
    toStatus: "",
    projectId: null,
    field: "",
    pendingValue: null,
    resolve: null,
  });

  // Refs for managing debounced updates
  const debounceTimeouts = useRef(new Map());

  // Ref to track recent updates to prevent realtime from overwriting optimistic updates
  const recentUpdates = useRef(new Map());

  // Handle tab changes with localStorage persistence and URL navigation
  const handleTabChange = useCallback((newTab) => {
    setActiveTab(newTab);
    localStorage.setItem("activeTab", newTab);
    // Navigate to the new tab URL
    navigationHelper.goToProjectTab(newTab);
  }, [navigationHelper]);
  
  // Update active tab when URL changes
  useEffect(() => {
    if (currentTab && currentTab !== activeTab) {
      setActiveTab(currentTab);
    }
  }, [currentTab, activeTab]);

  // Handle real-time project updates
  const handleRealtimeProjectUpdate = useCallback((updatedProject) => {
    // Check if we recently updated this project (within last 2 seconds)
    // If so, skip the realtime update to prevent double-flash
    const updateKey = updatedProject.id;
    const lastUpdate = recentUpdates.current.get(updateKey);
    if (lastUpdate && (Date.now() - lastUpdate) < 2000) {
      return;
    }

    // Update the project in state
    setProjects((prevProjects) =>
      prevProjects.map((project) =>
        project.id === updatedProject.id
          ? { ...project, ...updatedProject }
          : project,
      ),
    );
  }, []);

  // Set up real-time subscription for projects
  useRealtimeProjects(handleRealtimeProjectUpdate, true);

  // Status validation hook for ProjectService
  const statusValidationHook = useCallback(
    async (projectId, field, value) => {

      // Get current project to determine old status
      const project = projects.find((p) => p.id === projectId);
      const oldValue = project ? project.modelingStatus : null;

      // Check if FullSubitemModal has already approved an override
      // If so, skip the incomplete subitems validation, but still show exit comment modal
      const skipIncompleteValidation = window.__statusOverrideApproved;

      try {
        if (!skipIncompleteValidation) {
          // Run validation for incomplete subitems
          const validation = await StatusValidationService.validateStatusChange(
            projectId,
            oldValue,
            value,
          );

          if (!validation.isValid && validation.incompleteSubitems.length > 0) {

            // Show warning modal and return a promise that resolves when user chooses
            return new Promise((resolve) => {
              setStatusWarningData({
                incompleteSubitems: validation.incompleteSubitems,
                currentStatus: oldValue || "None",
                targetStatus: value,
                projectId,
                field,
                pendingValue: value,
                resolve, // Store the promise resolver
              });
              setShowStatusWarning(true);
            });
          }
        } else {
          console.log('🔄 Override approved - skipping incomplete subitems validation');
        }

        // Show exit comment modal for all modeling status changes (including overrides)
        // Skip if from "Ready to Export"
        console.log('🟢 Validation passed - checking exit modal. field:', field, 'oldValue:', oldValue, 'value:', value);
        if (field === 'modelingStatus' && oldValue && oldValue !== 'Ready to Export') {
          console.log('✅ Showing exit comment modal', skipIncompleteValidation ? '(after override)' : '(no incomplete subitems)');
          return new Promise((resolve) => {
            setExitCommentData({
              fromStatus: oldValue,
              toStatus: value,
              projectId,
              field,
              pendingValue: value,
              resolve, // Store the promise resolver
            });
            setShowExitCommentModal(true);
          });
        }

        console.log('🟢 Allowing change without exit modal');
        return true; // Allow the change
      } catch (validationError) {
        console.error("Status validation error:", validationError);
        return true; // Allow change on validation error (fail-safe)
      }
    },
    [projects],
  );

  // Set the validation hook in ProjectService on mount
  useEffect(() => {
    ProjectService.setStatusValidationHook(statusValidationHook);
    return () => {
      ProjectService.setStatusValidationHook(null);
    };
  }, [statusValidationHook]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [projectsData, clientsData, employeesData, filteredEmployeesData] =
        await Promise.all([
          ProjectService.getAllProjects(),
          ProjectService.getClients(),
          ProjectService.getEmployees(),
          ProjectService.getFilteredEmployees(),
        ]);

      // CRITICAL DEBUG: Check what we got from the service
      console.log("=== APP RECEIVED PROJECTS DATA ===");
      console.log(
        "First 3 projects:",
        projectsData.slice(0, 3).map((p) => ({
          id: p.id,
          name: p.projectName,
          lastModifiedBy: p.lastModifiedBy,
          lastModifiedAt: p.lastModifiedAt,
        })),
      );

      setProjects(projectsData);
      setClients(clientsData);
      setEmployees(employeesData);
      setFilteredEmployees(filteredEmployeesData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadUpdatedProject = useCallback(async (projectId) => {
    try {
      // Fetch single project with updated status
      const { data, error } = await supabase
        .from("projects")
        .select(
          `
          *,
          clients (id, name),
          contact:contacts (id, name, email, phone_number, role, client_id),
          ready_for_employee:employees!ready_for_employee_id (id, name, email),
          model_builder_employee:employees!model_builder_id (id, name, email)
        `,
        )
        .eq("id", projectId)
        .single();

      if (!error && data) {
        // Transform to match our format
        const transformedProject = ProjectService.transformProjectData(data);

        // Update the project in state
        setProjects((prevProjects) =>
          prevProjects.map((p) =>
            p.id === projectId ? transformedProject : p,
          ),
        );
      }
    } catch (err) {
      console.error("Error loading updated project:", err);
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, []);

  // Ctrl+Z undo functionality (up to 3 actions)
  useEffect(() => {
    const handleKeydown = (e) => {
      if (e.ctrlKey && e.key === "z" && undoHistory.length > 0) {
        e.preventDefault();

        // Get the most recent action
        const lastAction = undoHistory[undoHistory.length - 1];

        // Revert to the old value
        setProjects((prevProjects) =>
          prevProjects.map((project) =>
            project.id === lastAction.projectId
              ? { ...project, [lastAction.field]: lastAction.oldValue }
              : project,
          ),
        );

        // Update database with old value
        ProjectService.updateProject(
          lastAction.projectId,
          lastAction.field,
          lastAction.oldValue,
        ).catch((err) => {
          console.error("Undo failed:", err);
          setError("Undo failed. Reloading data.");
          loadInitialData();
        });

        // Remove the action from history
        setUndoHistory((prev) => prev.slice(0, -1));

        // Show toast
        setShowUndoToast(true);

        // Hide toast after 2 seconds
        setTimeout(() => setShowUndoToast(false), 2000);
      }
    };

    window.addEventListener("keydown", handleKeydown);
    return () => window.removeEventListener("keydown", handleKeydown);
  }, [undoHistory]);

  // No more filteredProjects - all filtering is done in the tabbed interface
  // This allows for instant tab switching and better performance

  const handleAddProject = useCallback(async (projectData) => {
    try {
      setOperationLoading(true);
      const newProject = await ProjectService.createProject(projectData);
      setProjects((prevProjects) => [newProject, ...prevProjects]);
      setShowAddForm(false);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setOperationLoading(false);
    }
  }, []);

  // Debounced update function with retry logic
  const performDatabaseUpdate = useCallback(
    async (
      projectId,
      field,
      value,
      retryCount = 0,
      needsStatusRecalc = false,
    ) => {
      const maxRetries = 2;

      console.log(
        `performDatabaseUpdate: Updating ${field} to ${value} for project ${projectId}`,
      );

      try {
        const updatedProject = await ProjectService.updateProject(
          projectId,
          field,
          value,
        );

        console.log("Database update response:", {
          projectId,
          field,
          requestedValue: value,
          returnedValue: updatedProject?.[field],
          fullProject: updatedProject,
        });

        // If status recalculation is needed, trigger it
        if (needsStatusRecalc && updatedProject) {
          console.log("Triggering status recalculation for project", projectId);
          const { error: calcError } = await supabase.rpc(
            "calculate_stage_status",
            { project_id: projectId },
          );

          if (calcError) {
            console.error("Status recalculation failed:", calcError);
          } else {
            // Reload the project to get the updated status
            await loadUpdatedProject(projectId);
          }
        } else if (updatedProject) {
          // Update the project with the actual data from the server
          setProjects((prevProjects) =>
            prevProjects.map((project) =>
              project.id === projectId ? updatedProject : project,
            ),
          );
        }

        // Success - remove from updating set
        setUpdatingProjects((prev) => {
          const newSet = new Set(prev);
          newSet.delete(`${projectId}-${field}`);
          return newSet;
        });
        setError(null);
      } catch (err) {
        console.error(`Update failed (attempt ${retryCount + 1}):`, err);

        if (retryCount < maxRetries) {
          // Retry after exponential backoff
          setTimeout(
            () => {
              performDatabaseUpdate(projectId, field, value, retryCount + 1);
            },
            Math.pow(2, retryCount) * 1000,
          );
        } else {
          // Max retries reached - remove from updating set and show error
          setUpdatingProjects((prev) => {
            const newSet = new Set(prev);
            newSet.delete(`${projectId}-${field}`);
            return newSet;
          });
          setError("Update failed after retries. Please try again.");
        }
      }
    },
    [loadUpdatedProject],
  );

  const handleUpdateProject = useCallback(
    async (projectId, field, value) => {
      console.log("🔵 APP handleUpdateProject CALLED:", {
        timestamp: Date.now(),
        projectId,
        field,
        value,
        valueType: typeof value,
        currentUpdatingProjects: Array.from(updatingProjects),
        projectsCount: projects.length,
      });

      // Prevent concurrent updates on same project field
      const updateKey = `${projectId}-${field}`;
      if (updatingProjects.has(updateKey)) {
        console.log(`🟡 APP Update already in progress for ${updateKey}`);
        return;
      }

      // Find the current project to get the old value
      const project = projects.find((p) => p.id === projectId);
      // Map database field names to UI field names for comparison
      const uiField = field === "client_id" ? "clientId" : field;
      const oldValue = project ? project[uiField] : null;

      // Enhanced logging for dropdown persistence debugging
      console.log("🔵 APP handleUpdateProject - Project found:", {
        projectId,
        field,
        value,
        oldValue,
        uiField,
        projectName: project?.projectName,
        projectFound: !!project,
        projectIsHighPriority: project?.isHighPriority,
      });

      // Don't update if value hasn't changed (handle type conversion)
      // Special handling for boolean values to ensure proper comparison
      if (field === "isHighPriority" || field === "isSoftHold") {
        const oldBool = Boolean(oldValue);
        const newBool = Boolean(value);
        console.log(`🔵 APP ${field} comparison:`, {
          oldValue,
          value,
          oldBool,
          newBool,
          willSkip: oldBool === newBool,
        });
        if (oldBool === newBool) {
          console.log("🟡 APP Boolean value unchanged, skipping update");
          return;
        }
      } else if (
        oldValue === value ||
        (oldValue && value && String(oldValue) === String(value))
      ) {
        console.log("🟡 APP Value unchanged, skipping update");
        return;
      }

      // Check if we need to recalculate status after this update
      const needsStatusRecalc =
        field === "modelingStatus" || field === "modelingState";

      // Store action for undo history
      setUndoHistory((prev) => {
        const newHistory = [
          ...prev,
          {
            projectId,
            field,
            oldValue,
            newValue: value,
            timestamp: Date.now(),
          },
        ];
        // Keep only the last 3 actions
        return newHistory.slice(-3);
      });

      // 1. Update UI IMMEDIATELY (optimistic update)
      // IMPORTANT: Also update lastModifiedBy with current user email for optimistic update
      const {
        data: { user },
      } = await supabase.auth.getUser();

      console.log("🔵 APP OPTIMISTIC UPDATE - Before setProjects:", {
        projectId,
        field,
        value,
        projectsLength: projects.length,
      });

      // Record this update to prevent realtime from overwriting it immediately
      recentUpdates.current.set(projectId, Date.now());

      // Clean up after 3 seconds to prevent memory leak
      setTimeout(() => {
        recentUpdates.current.delete(projectId);
      }, 3000);

      setProjects((prevProjects) => {
        console.log("🔵 APP setProjects callback:", {
          prevProjectsLength: prevProjects.length,
          projectId,
          field,
          value,
          projectExists: prevProjects.some((p) => p.id === projectId),
        });

        const updatedProjects = prevProjects.map((project) => {
          if (project.id === projectId) {
            // Map database field names to UI field names for optimistic update
            const uiField = field === "client_id" ? "clientId" : field;
            const updatedProject = {
              ...project,
              [uiField]: value,
              // Optimistically update the last modified fields
              lastModifiedBy: user?.email || "Unknown",
              lastModifiedAt: new Date().toISOString(),
            };

            console.log("🔵 APP Updating project in state:", {
              oldIsHighPriority: project.isHighPriority,
              newIsHighPriority: updatedProject.isHighPriority,
              field,
              value,
            });

            return updatedProject;
          }
          return project;
        });

        return updatedProjects;
      });

      // 2. Add to updating set for visual feedback
      setUpdatingProjects((prev) => new Set(prev).add(updateKey));

      // 3. Clear existing timeout for this field
      if (debounceTimeouts.current.has(updateKey)) {
        clearTimeout(debounceTimeouts.current.get(updateKey));
      }

      // 4. Set new debounced timeout
      const timeoutId = setTimeout(() => {
        debounceTimeouts.current.delete(updateKey);
        performDatabaseUpdate(projectId, field, value, 0, needsStatusRecalc);
      }, 500);

      debounceTimeouts.current.set(updateKey, timeoutId);
    },
    [projects, updatingProjects, performDatabaseUpdate],
  );

  const handleDeleteProject = useCallback(async (projectId) => {
    if (window.confirm("Are you sure you want to delete this project?")) {
      try {
        setOperationLoading(true);
        await ProjectService.deleteProject(projectId);
        setProjects((prevProjects) =>
          prevProjects.filter((project) => project.id !== projectId),
        );
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setOperationLoading(false);
      }
    }
  }, []);

  // Filter handlers removed - filtering is now handled by tabs

  const handleRowClick = useCallback((project, type = "project") => {
    if (type === "contact" && project.contact) {
      setSelectedContact(project.contact);
      setContactModalOpen(true);
    } else if (type === "subitems") {
      // Eye button - open FullSubitemModal (with status progress)
      setSelectedProject(project);
      setIsModalOpen(true);
    } else if (type === "details") {
      // Info button - open ProjectDetailsModal
      setSelectedProject(project);
      setIsProjectDetailsModalOpen(true);
    } else {
      // Default - open FullSubitemModal
      setSelectedProject(project);
      setIsModalOpen(true);
    }
  }, []);

  const handleCloseModal = useCallback(() => {
    // Navigate back to the current tab without project ID
    navigationHelper.goToProjectTab(activeTab);

    setIsModalOpen(false);
    setSelectedProject(null);
  }, [navigationHelper, activeTab]);

  const handleCloseProjectDetailsModal = useCallback(() => {
    setIsProjectDetailsModalOpen(false);
    setSelectedProject(null);
  }, []);

  const handleCloseSubitemsModal = useCallback(() => {
    setIsSubitemsModalOpen(false);
    setSelectedProject(null);
  }, []);

  const handleCloseContactModal = useCallback(() => {
    setContactModalOpen(false);
    setSelectedContact(null);
  }, []);

  // Status advancement warning modal handlers
  const handleOverrideStatusWarning = useCallback(() => {
    console.log('🔵 Override Warning - statusWarningData:', statusWarningData);
    setShowStatusWarning(false);

    // After warning, check if we should show exit comment modal
    const fromStatus = statusWarningData.currentStatus;
    console.log('🔵 fromStatus:', fromStatus, 'field:', statusWarningData.field);

    if (fromStatus && fromStatus !== 'Ready to Export' && fromStatus !== 'None') {
      console.log('✅ Showing exit comment modal');
      // Show exit comment modal
      setExitCommentData({
        fromStatus: fromStatus,
        toStatus: statusWarningData.targetStatus,
        projectId: statusWarningData.projectId,
        field: statusWarningData.field,
        pendingValue: statusWarningData.pendingValue,
        resolve: statusWarningData.resolve, // Pass the resolver to exit modal
      });
      setShowExitCommentModal(true);
    } else {
      console.log('❌ Skipping exit comment modal - fromStatus:', fromStatus);
      // No exit comment needed, resolve immediately
      if (statusWarningData.resolve) {
        statusWarningData.resolve(true);
      }
    }

    // Clear warning data
    setStatusWarningData({
      incompleteSubitems: [],
      currentStatus: "",
      targetStatus: "",
      projectId: null,
      field: "",
      pendingValue: null,
      resolve: null,
    });
  }, [statusWarningData]);

  const handleCloseStatusWarning = useCallback(() => {
    console.log("🔍 APP: Closing status warning modal - user cancelled");
    setShowStatusWarning(false);

    // Resolve the promise to reject the status change
    if (statusWarningData.resolve) {
      statusWarningData.resolve(false);
    }

    setStatusWarningData({
      incompleteSubitems: [],
      currentStatus: "",
      targetStatus: "",
      projectId: null,
      field: "",
      pendingValue: null,
      resolve: null,
    });
  }, [statusWarningData]);

  // Modeling status exit comment modal handlers
  const handleExitCommentSubmit = useCallback(async (comment) => {
    setShowExitCommentModal(false);

    try {
      // Save comment to database if provided
      if (comment && comment.trim()) {
        const { data: { user } } = await supabase.auth.getUser();
        const userEmail = user?.email;

        if (userEmail) {
          await ProjectService.saveModelingStatusComment(
            exitCommentData.projectId,
            exitCommentData.fromStatus,
            comment,
            userEmail
          );
          console.log('✅ Modeling status comment saved successfully');
        } else {
          console.warn('Could not get user email, comment not saved');
        }
      }

      // Resolve the promise to allow the status change
      if (exitCommentData.resolve) {
        exitCommentData.resolve(true);
      }
    } catch (err) {
      console.error('Error saving exit comment:', err);
      // Still allow the status change even if comment save fails
      if (exitCommentData.resolve) {
        exitCommentData.resolve(true);
      }
    } finally {
      // Clear exit comment data
      setExitCommentData({
        fromStatus: "",
        toStatus: "",
        projectId: null,
        field: "",
        pendingValue: null,
        resolve: null,
      });
    }
  }, [exitCommentData]);

  const handleExitCommentSkip = useCallback(() => {
    setShowExitCommentModal(false);

    // Resolve the promise to allow the status change
    if (exitCommentData.resolve) {
      exitCommentData.resolve(true);
    }

    // Clear exit comment data
    setExitCommentData({
      fromStatus: "",
      toStatus: "",
      projectId: null,
      field: "",
      pendingValue: null,
      resolve: null,
    });
  }, [exitCommentData]);

  const handleExitCommentClose = useCallback(() => {
    setShowExitCommentModal(false);

    // Resolve the promise to reject the status change (user cancelled)
    if (exitCommentData.resolve) {
      exitCommentData.resolve(false);
    }

    // Clear exit comment data
    setExitCommentData({
      fromStatus: "",
      toStatus: "",
      projectId: null,
      field: "",
      pendingValue: null,
      resolve: null,
    });
  }, [exitCommentData]);

  const handleModalUpdate = useCallback(async (projectId, field, value) => {
    try {
      // Don't set global loading for modal updates - use local loading instead
      const updatedProject = await ProjectService.updateProject(
        projectId,
        field,
        value,
      );
      setProjects((prevProjects) =>
        prevProjects.map((project) =>
          project.id === projectId ? updatedProject : project,
        ),
      );
      // Update the selected project if it's the one being updated
      setSelectedProject((prevSelected) =>
        prevSelected && prevSelected.id === projectId
          ? updatedProject
          : prevSelected,
      );
      setError(null);
    } catch (err) {
      setError(err.message);
      throw err; // Let modal handle the error state
    }
  }, []);

  if (loading) {
    return (
      <div className="app">
        <div className="app-container">
          <SkeletonLoader type="table" rows={10} />
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <TopProgressBar isLoading={loading} />
      <div className="app-container">
        {error && (
          <ErrorMessage
            message={error}
            onRetry={loadInitialData}
            onDismiss={() => setError(null)}
          />
        )}

        <main className="app-main">
          <TabbedProjectInterface
            projects={projects}
            onUpdateProject={handleUpdateProject}
            onDeleteProject={handleDeleteProject}
            onRowClick={handleRowClick}
            loading={false}
            searchTerm={searchTerm}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            updatingProjects={updatingProjects}
            clients={clients}
            employees={employees}
            filteredEmployees={filteredEmployees}
            urlProjectId={urlProjectId}
          />

          {projects.length === 0 && !loading && (
            <div className="empty-state">
              <p>No projects found.</p>
              <p>Get started by adding your first project.</p>
            </div>
          )}
        </main>

        {showAddForm && (
          <AddProjectForm
            onAddProject={handleAddProject}
            onCancel={() => setShowAddForm(false)}
            clients={clients}
            loading={operationLoading}
          />
        )}

        {/* Only render modals when they're actually open */}
        {/* Eye button - FullSubitemModal with status progress */}
        {isModalOpen && (
          <FullSubitemModal
            project={selectedProject}
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            onUpdateProject={handleModalUpdate}
            employees={filteredEmployees}
          />
        )}

        {/* Info button - ProjectDetailsModal */}
        {isProjectDetailsModalOpen && (
          <ProjectDetailsModal
            project={selectedProject}
            isOpen={isProjectDetailsModalOpen}
            onClose={handleCloseProjectDetailsModal}
            onUpdateProject={handleModalUpdate}
          />
        )}

        {/* SubitemsModal - not used currently */}
        {isSubitemsModalOpen && (
          <SubitemsModal
            project={selectedProject}
            isOpen={isSubitemsModalOpen}
            onClose={handleCloseSubitemsModal}
          />
        )}

        {contactModalOpen && (
          <ContactModal
            contact={selectedContact}
            isOpen={contactModalOpen}
            onClose={handleCloseContactModal}
          />
        )}

        {/* Status Advancement Warning Modal */}
        <StatusAdvancementWarningModal
          isOpen={showStatusWarning}
          onClose={handleCloseStatusWarning}
          onOverride={handleOverrideStatusWarning}
          incompleteSubitems={statusWarningData.incompleteSubitems}
          currentStatus={statusWarningData.currentStatus}
          targetStatus={statusWarningData.targetStatus}
        />

        {/* Modeling Status Exit Comment Modal */}
        <ModelingStatusExitCommentModal
          isOpen={showExitCommentModal}
          onClose={handleExitCommentClose}
          onSubmit={handleExitCommentSubmit}
          onSkip={handleExitCommentSkip}
          fromStatus={exitCommentData.fromStatus}
          toStatus={exitCommentData.toStatus}
          employees={employees}
        />

        {/* Undo Toast */}
        {showUndoToast && <div className="undo-toast">Change undone</div>}
      </div>
    </div>
  );
};

// Error Boundary to catch React errors
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.log('🚨 [ErrorBoundary] Error caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: '20px',
          color: 'red',
          background: 'white',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <h2>🚨 Application Error Detected!</h2>
          <p>The Office Admin Dashboard encountered an error:</p>
          <details style={{ marginTop: '10px', background: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
            <summary>Error Details (Click to expand)</summary>
            <pre style={{ whiteSpace: 'pre-wrap', marginTop: '10px' }}>
              {this.state.error && this.state.error.toString()}
            </pre>
          </details>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const MainApp = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const {
    isOfficeAdmin,
    isTakeoffs,
    canAccessModelBuilding,
    canAccessOfficeAdmin,
    canAccessOperationsDashboards,
    loading: userRoleLoading,
  } = useUserRole();
  
  // Parse current URL
  const { page: urlPage } = parseCurrentUrl(location.pathname);

  // Determine default page based on user role
  const getDefaultPage = () => {
    // Use URL page if available
    if (urlPage) {
      return urlPage;
    }
    
    const savedPage = localStorage.getItem("currentPage");

    // For Takeoffs role, always default to takeoffs page
    if (isTakeoffs) {
      return "takeoffs";
    }

    // For Office Admin role, always default to office-admin
    if (isOfficeAdmin && !canAccessModelBuilding) {
      return "office-admin";
    }

    // For other roles, use saved page or default to projects
    return savedPage || "projects";
  };

  const [currentPage, setCurrentPage] = useState(getDefaultPage);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem("sidebarCollapsed") === "true";
  });
  const [showProfile, setShowProfile] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isPageTransitioning, setIsPageTransitioning] = useState(false);

  // Add ref to prevent rapid consecutive redirections
  const lastNavigationRef = useRef(0);
  const NAVIGATION_DEBOUNCE_MS = 1000; // Prevent redirections more than once per second

  // Handle legacy URL parameters and convert to new routing format
  useEffect(() => {
    const legacyUrl = handleLegacyUrl();
    if (legacyUrl && legacyUrl !== location.pathname) {
      // Navigate to the new URL format
      navigate(legacyUrl, { replace: true });
      return;
    }
    
    // Update current page based on URL
    if (urlPage !== currentPage) {
      setCurrentPage(urlPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, navigate, urlPage]); // currentPage intentionally excluded to prevent infinite loop

  // Redirect users based on their role restrictions
  useEffect(() => {
    // Don't redirect while user permissions are still loading
    if (userRoleLoading) {
      console.log("⏳ User permissions loading - skipping redirect");
      return;
    }

    // Prevent rapid consecutive redirections
    const now = Date.now();
    if (now - lastNavigationRef.current < NAVIGATION_DEBOUNCE_MS) {
      console.log("🟡 Navigation debounced - skipping redirect");
      return;
    }

    // Allow Takeoffs users to access feedback, takeoffs, and contacts pages
    if (isTakeoffs) {
      const allowedPages = ["feedback", "takeoffs", "contacts"];
      if (!allowedPages.includes(currentPage)) {
        console.log("🔄 Redirecting Takeoffs user from", currentPage, "to takeoffs");
        lastNavigationRef.current = now;
        setCurrentPage("takeoffs");
        localStorage.setItem("currentPage", "takeoffs");
      }
    }
    // Redirect Office Admin users to office-admin if they try to access restricted pages
    else if (isOfficeAdmin && !canAccessModelBuilding) {
      // Office Admin can access: feedback, office-admin, takeoffs
      const allowedPages = ["feedback", "office-admin", "takeoffs"];
      if (!allowedPages.includes(currentPage)) {
        console.log("🔄 Redirecting Office Admin from", currentPage, "to office-admin");
        lastNavigationRef.current = now;
        setCurrentPage("office-admin");
        localStorage.setItem("currentPage", "office-admin");
      }
    }
    // Redirect users without model building access away from on-hold-board
    else if (!canAccessModelBuilding && currentPage === "on-hold-board") {
      console.log("🔄 Redirecting user without model building access from on-hold-board to projects");
      lastNavigationRef.current = now;
      setCurrentPage("projects");
      localStorage.setItem("currentPage", "projects");
    }
  }, [isOfficeAdmin, isTakeoffs, canAccessModelBuilding, currentPage, userRoleLoading]);

  // Listen for sidebar collapse events
  useEffect(() => {
    const handleSidebarCollapse = (event) => {
      setSidebarCollapsed(event.detail.isCollapsed);
    };

    document.addEventListener("sidebarCollapse", handleSidebarCollapse);
    return () => {
      document.removeEventListener("sidebarCollapse", handleSidebarCollapse);
    };
  }, []);

  // Save current page to localStorage and navigate
  const handlePageChange = useCallback(
    (newPage) => {
      // Allow Takeoffs users to navigate to feedback, takeoffs, and contacts pages
      if (isTakeoffs) {
        const allowedPages = ["feedback", "takeoffs", "contacts"];
        if (!allowedPages.includes(newPage)) {
          return;
        }
      }

      // Show progress bar during page transition
      setIsPageTransitioning(true);

      setCurrentPage(newPage);
      localStorage.setItem("currentPage", newPage);

      // Navigate to the new page URL
      navigate(generatePageUrl(newPage));

      // Hide progress bar after transition
      setTimeout(() => setIsPageTransitioning(false), 600);
    },
    [isTakeoffs, navigate],
  );

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleProfileClick = () => {
    setShowProfile(true);
  };

  return (
    <div className="app-layout">
      <TopProgressBar isLoading={isPageTransitioning} />
      <Sidebar
        currentPage={currentPage}
        onPageChange={handlePageChange}
        isOpen={sidebarOpen}
        onToggle={toggleSidebar}
      />
      <div
        className={`main-content-wrapper ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}
      >
        <Header
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onMenuToggle={toggleSidebar}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onProfileClick={handleProfileClick}
        />
        <div className="page-content">
          {currentPage === "feedback" && <FeedbackTable />}
          {currentPage === "projects" && (
            <Dashboard
              searchTerm={searchTerm}
            />
          )}
          {currentPage === "project-setup" && <ProjectSetupBoard />}
          {currentPage === "subitem-templates" && canAccessModelBuilding && (
            <SubitemTemplateTable />
          )}
          {currentPage === "subitem-templates-new" && canAccessModelBuilding && (
            <SubitemTemplateManager />
          )}
          {currentPage === "project-emails" && <ProjectEmailsBoard />}
          {currentPage === "contacts" && <ContactsTable />}
          {currentPage === "clients" && <ClientsTable />}
          {currentPage === "discrepancies" && <DiscrepancyTable />}
          {currentPage === "takeoffs" && <TakeoffsTable />}
          {currentPage === "operations-dashboard" && <OperationsDashboard />}
          {currentPage === "model-building-manager" && <ModelBuildingManagerDashboard />}
          {currentPage === "manager-dashboard" && <ManagerDashboard />}
          {currentPage === "office-admin" && (
            <ErrorBoundary>
              <OfficeAdminDashboard />
            </ErrorBoundary>
          )}
          {currentPage === "invoicing" && canAccessOfficeAdmin && <InvoicingBoard />}
          {currentPage === "pricing" && canAccessOfficeAdmin && <PricingDashboard />}
          {currentPage === "remote-support" && canAccessOfficeAdmin && <RemoteSupportTable />}
          {currentPage === "on-hold-board" && canAccessModelBuilding && (
            <OnHoldBoard />
          )}
          {currentPage === "admin" && <AdminPanel />}
          {currentPage === "planner-config" && canAccessOperationsDashboards && <PlannerConfigAdmin />}
          {currentPage === "employees" && <EmployeesManagement />}
          {currentPage === "payroll" && canAccessOfficeAdmin && <PayrollBoard />}
          {currentPage === "stats" && <ProjectStats />}
          {currentPage === "profit-analysis" && <ProfitAnalysis />}
          {currentPage === "kpi-board" && <KPIBoard />}
        </div>
      </div>
      <UserProfile isOpen={showProfile} onClose={() => setShowProfile(false)} />
    </div>
  );
};

// Global error interceptor for auth errors with race condition prevention
class AuthErrorInterceptor {
  constructor() {
    this.originalFetch = window.fetch;
    this.isRefreshing = false;
    this.refreshPromise = null;
    this.setupInterceptor();
  }

  setupInterceptor() {
    // Bind originalFetch to window context to avoid "Illegal invocation" error
    const originalFetch = this.originalFetch.bind(window);

    window.fetch = async (...args) => {
      try {
        const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';

        // Skip interception for Supabase URLs to prevent loops
        if (url.includes('db.ab-civil.com') || url.includes('supabase')) {
          return originalFetch(...args);
        }

        const response = await originalFetch(...args);

        // Handle auth errors globally
        if (response.status === 401 || response.status === 403) {
          console.warn(`[AuthInterceptor] Auth error detected (${response.status}) for ${url}`);

          // Prevent concurrent refresh attempts
          if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshPromise = supabase.auth.refreshSession();

            const { error } = await this.refreshPromise;
            this.isRefreshing = false;
            this.refreshPromise = null;

            if (!error) {
              console.log('[AuthInterceptor] Session refreshed, retrying request');
              // Retry the original request with new token
              return originalFetch(...args);
            } else {
              console.error('[AuthInterceptor] Failed to refresh session:', error);
            }
          } else {
            // Wait for existing refresh to complete
            console.log('[AuthInterceptor] Waiting for existing refresh...');
            const { error } = await this.refreshPromise;
            if (!error) {
              return originalFetch(...args);
            }
          }
        }

        return response;
      } catch (error) {
        // Log network errors for debugging
        if (error.message?.toLowerCase().includes('network') ||
            error.message?.toLowerCase().includes('fetch')) {
          console.error('[AuthInterceptor] Network error:', error.message);
        }
        throw error;
      }
    };
  }
}

// Initialize the interceptor
new AuthErrorInterceptor();

function App() {
  return (
    <AuthProvider>
      <DataProvider>
        <Router>
          <UsageTracker />
          <Routes>
            {/* OAuth callback route - must be outside ProtectedRoute */}
            <Route path="/callback" element={<AuthCallback />} />
            {/* Planner demo - standalone page outside main app shell */}
            <Route path="/planner-demo" element={
              <ProtectedRoute>
                <PlannerConfigDemo />
              </ProtectedRoute>
            } />
            {/* Peer Reviews - hidden standalone page */}
            <Route path="/peer-reviews" element={<PeerReviews />} />
            {/* Model Planner - full page wizard for production use */}
            <Route path="/planner/:projectId" element={
              <ProtectedRoute>
                <ModelPlannerPage />
              </ProtectedRoute>
            } />
            <Route path="/*" element={
              <ProtectedRoute>
                <MainApp />
              </ProtectedRoute>
            } />
          </Routes>
          <SessionWarning />
          <NetworkMonitor />
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: 'var(--dark-surface)',
                color: 'var(--gray-50)',
                border: '1px solid var(--gray-700)',
              },
              success: {
                iconTheme: {
                  primary: 'var(--green-400)',
                  secondary: 'var(--dark-surface)',
                },
              },
              error: {
                iconTheme: {
                  primary: 'var(--red-400)',
                  secondary: 'var(--dark-surface)',
                },
              },
            }}
          />
        </Router>
      </DataProvider>
    </AuthProvider>
  );
}

export default App;
